/**
 * <facet-filters-form> — progressive enhancement for the filter sidebar.
 *
 * Without JS the sidebar is a plain GET form (checkbox change + "Apply"
 * submits and reloads). With JS we serialise the form, fetch the same
 * section via the Section Rendering API (`?section_id=…`) and swap in the
 * new product grid and sidebar, then push the URL so filters are
 * shareable and the back button works.
 *
 * Markup contract (see sections/main-collection.liquid):
 *   <facet-filters-form data-section="{{ section.id }}">
 *     <button data-facets-toggle aria-controls="…">   (mobile only)
 *     <aside id="Facets-{{ section.id }}">  … {% render 'facets' %} …
 *     <div id="Results-{{ section.id }}">   … grid + pagination …
 *   </facet-filters-form>
 */
class FacetFiltersForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.debounceTimer = null;
    this.abortController = null;

    this.addEventListener('input', (e) => this.onInput(e));
    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('submit', (e) => this.onSubmit(e));
    this.addEventListener('click', (e) => this.onClick(e));
    window.addEventListener('popstate', () => this.render(window.location.search, false));

    this.hideApplyButtons();
  }

  get form() {
    return this.querySelector('form.facets');
  }

  /* ---- events ---------------------------------------------------------- */

  onInput(e) {
    // Price fields fire on every keystroke — wait for the user to pause.
    if (!e.target.matches('[data-facets-price]')) return;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.applyForm(), 600);
  }

  onChange(e) {
    if (e.target.matches('[data-facets-price]')) {
      clearTimeout(this.debounceTimer);
    }
    if (e.target.closest('form.facets')) this.applyForm();
  }

  onSubmit(e) {
    if (e.target.matches('form.facets')) {
      e.preventDefault();
      this.applyForm();
    }
  }

  onClick(e) {
    const toggle = e.target.closest('[data-facets-toggle]');
    if (toggle) {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      this.classList.toggle('facets-open', open);
      return;
    }

    // Active-filter chips, "Clear all", and pagination links all point at a
    // URL that already carries the right query string — fetch instead of
    // navigating.
    const link = e.target.closest('[data-facets-link], .collection__pagination a, .search__pagination a');
    if (!link || !link.href) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    this.render(url.search, true);
  }

  /* ---- fetching -------------------------------------------------------- */

  applyForm() {
    const form = this.form;
    if (!form) return;
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (value !== '') params.append(key, value);
    }
    // Any change to filters or sort invalidates the current page number.
    params.delete('page');
    this.render('?' + params.toString(), true);
  }

  async render(search, pushState) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams(search);
    params.set('section_id', this.sectionId);
    const fetchUrl = window.location.pathname + '?' + params.toString();

    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(fetchUrl, { signal: this.abortController.signal });
      if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
      const html = await res.text();
      this.swap(html);
      if (pushState) {
        const clean = new URLSearchParams(search);
        clean.delete('section_id');
        const qs = clean.toString();
        history.pushState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // Fall back to a full navigation so the user is never stuck.
        window.location.search = search;
      }
    } finally {
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  }

  swap(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const ids = ['Facets-' + this.sectionId, 'Results-' + this.sectionId];

    // Remember which groups are collapsed and what has focus so the swap is
    // invisible to a keyboard user mid-flow.
    const closedGroups = new Set(
      Array.from(this.querySelectorAll('details[data-facets-group]:not([open])')).map((d) => d.dataset.facetsGroup)
    );
    const activeId = document.activeElement && document.activeElement.id;
    const focusWasInside = this.contains(document.activeElement);

    ids.forEach((id) => {
      const current = document.getElementById(id);
      const next = doc.getElementById(id);
      if (current && next) current.replaceWith(next);
    });

    // The count is a live region: update its text in place (a replaced node
    // is not announced) so screen readers hear the new number of results.
    const countId = 'ResultsCount-' + this.sectionId;
    const count = document.getElementById(countId);
    const nextCount = doc.getElementById(countId);
    if (count && nextCount) count.textContent = nextCount.textContent.trim();

    this.querySelectorAll('details[data-facets-group]').forEach((d) => {
      if (closedGroups.has(d.dataset.facetsGroup)) d.removeAttribute('open');
    });

    // Put focus back where it was. Chips and "Clear all" have no id and are
    // gone after the swap, so fall back to the results count.
    const restored = activeId && document.getElementById(activeId);
    if (restored && restored.tagName !== 'BODY') {
      restored.focus({ preventScroll: true });
    } else if (focusWasInside && count) {
      count.setAttribute('tabindex', '-1');
      count.focus({ preventScroll: true });
    }

    this.hideApplyButtons();

    // Scroll the grid into view when it's above the fold on mobile.
    const results = document.getElementById('Results-' + this.sectionId);
    if (results && results.getBoundingClientRect().top < 0) {
      results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  hideApplyButtons() {
    this.querySelectorAll('[data-facets-apply]').forEach((btn) => (btn.hidden = true));
  }
}

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
